import React, { useMemo, useState } from 'react'
import { useToggleSuggestions } from 'src/components/async/suggestions/useToggleSuggestions'
import { TextButton } from 'src/components/Reusable/Buttons/TextButton'
import {
  SmallStyledSubsectionAccordionIcon,
  StyledSubsectionAccordionIcon,
} from 'src/components/Reusable/Accordions/ShowPage/Subsections/SubsectionAccordion.styles'
import {
  faRefresh,
  faRotateLeft,
  faWandMagicSparkles,
} from '@fortawesome/free-solid-svg-icons'
import showdown from 'showdown'
import { SuggestionsObject } from 'src/components/async/suggestions/suggestions.helpers'
import { TabType, Tabs } from 'src/components/Reusable/Tabs'
import { Markup } from 'src/components/Reusable/Text/Markup'
import { useAnalytics } from 'src/analytics/useAnalytics'
import { faComment } from '@fortawesome/free-regular-svg-icons'
import { useTypewriter } from 'src/Utils/globalHooks'
import { getAILoadingMessage } from 'src/Utils/globalHelpers'
import {
  AICopilotGuidanceContainer,
  RestoreGuidanceContainer,
  GuidanceAICopilotIconEast,
} from './ControlsShowItemGuidance.styles'

export const SubsectionAccordionMagicIcon = ({
  className,
}: {
  className?: string
}): JSX.Element => {
  return (
    <StyledSubsectionAccordionIcon
      icon={faWandMagicSparkles}
      className={className}
    />
  )
}

export const SubsectionAccordionCommentIcon = ({
  className,
}: {
  className?: string
}): JSX.Element => {
  return (
    <StyledSubsectionAccordionIcon icon={faComment} className={className} />
  )
}

export const SubsectionAccordionRestoreIcon = (): JSX.Element => {
  return <SmallStyledSubsectionAccordionIcon icon={faRotateLeft} />
}

export const SubsectionAccordionRefreshIcon = (): JSX.Element => {
  return <SmallStyledSubsectionAccordionIcon icon={faRefresh} $isRefresh />
}

export const RestoreGuidance = ({
  controlId,
  updateRestore,
}: {
  controlId: string
  updateRestore: () => void
}): JSX.Element => {
  const { restoreSuggestions } = useToggleSuggestions({ controlId })
  const { track, events } = useAnalytics()
  return (
    <RestoreGuidanceContainer>
      <TextButton
        onClick={async (e) => {
          e.stopPropagation()
          track(events.C_SUGG_RESTORE_GUIDANCE, { controlId: controlId })
          await restoreSuggestions()
          updateRestore()
        }}
      >
        <>
          <SubsectionAccordionRestoreIcon />
          Restore Guidance
        </>
      </TextButton>
    </RestoreGuidanceContainer>
  )
}

export const SubsectionSuggestionBody = ({
  value,
  isAI,
  customMdConverter,
}: {
  value: string
  isAI: boolean
  customMdConverter?: showdown.Converter
}): JSX.Element => (
  <>
    {isAI ? (
      <AICopilotGuidanceContainer>
        <GuidanceAICopilotIconEast />
        <Markup markdown={value} customConverter={customMdConverter} />
      </AICopilotGuidanceContainer>
    ) : (
      <Markup markdown={value} customConverter={customMdConverter} />
    )}
  </>
)

export const SubsectionSuggestion = ({
  suggestions,
  controlId,
  isCustom,
  isAI,
  isTestProcedures,
}: {
  suggestions: SuggestionsObject
  controlId?: string
  isCustom?: boolean
  isAI?: boolean
  isTestProcedures?: boolean
}): JSX.Element => {
  const { track, events } = useAnalytics()
  const [selectedTab, setSelectedTab] = useState<number>(0)
  const duplicates = suggestions.duplicates
  const suggestionArray = Object.entries(suggestions)
  const suggestionValue = Object.values(suggestions)
  const suggestionTabs: TabType[] = []

  // Create converter with simpleLineBreaks for test procedures
  const converter = useMemo(() => {
    if (!isTestProcedures) return undefined

    const testProceduresConverter = new showdown.Converter()
    testProceduresConverter.setFlavor('github')
    testProceduresConverter.setOption('simpleLineBreaks', true)
    testProceduresConverter.setOption('simplifiedAutoLink', false)
    return testProceduresConverter
  }, [isTestProcedures])

  for (const [key, value] of suggestionArray) {
    if (key === 'duplicates') {
      continue
    }

    const suggestionTab = {
      title: key,
      body: (
        <SubsectionSuggestionBody
          value={value}
          isAI={isAI || false}
          customMdConverter={converter}
        />
      ),
    }
    suggestionTabs.push(suggestionTab)
  }
  // If there is only one suggestion, display it alone
  const showSingular = suggestionTabs.length === 1
  // If there is more than one unique suggestion, display the tabs
  const showTabs = suggestionTabs.length > 1
  const onSelect = (e: string | null): void => {
    setSelectedTab(Number(e))
    track(events.C_SUGG_SWITCH_TABS, {
      controlId,
      tab: suggestionTabs[Number(e)].title,
    })
  }

  return (
    <>
      {isCustom ? (
        <Markup
          markdown={suggestionValue[0] || 'None'}
          customConverter={converter}
        />
      ) : (
        <>
          {duplicates && (
            <SubsectionSuggestionBody
              value={duplicates}
              isAI={isAI || false}
              customMdConverter={converter}
            />
          )}
          {showSingular && suggestionTabs[0].body}
        </>
      )}
      {!isCustom && showTabs && (
        <Tabs
          tabs={suggestionTabs}
          onSelect={onSelect}
          defaultActiveKey={selectedTab}
        />
      )}
      {!duplicates && !showSingular && !showTabs && (
        <Markup markdown="We couldn't find a match. Please add your own." />
      )}
    </>
  )
}

export const AICopilotLoading = (): JSX.Element => {
  const message = useMemo(() => getAILoadingMessage(), [])
  const ellipsis = useTypewriter({
    text: '...',
    interval: 500,
    shouldLoop: true,
  })
  return (
    <AICopilotGuidanceContainer>
      {message + ellipsis}
    </AICopilotGuidanceContainer>
  )
}
